import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { badgePayStationStateOptions } from 'src/features/cards/utils/constants';
import { useListAllParkings } from 'src/features/payStations/api/useListAllParkings.ts';
import { useListPayStations } from 'src/features/payStations/api/useListPaystations';
import payStationsMessages from 'src/features/payStations/utils/messages';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import useRowSelection from 'src/registry/hooks/useRowSelection';

import type { PayStationDevice } from 'src/features/payStations/types';

const PAY_STATION_DEVICE_PANEL_NAME = 'pay-station-device-panel';

const AllDevicesTable = () => {
    const { formatMessage } = useIntl();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'enter_datetime', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'pay_station_devices' });
    const rowSelection = useRowSelection({ sidePanelName: PAY_STATION_DEVICE_PANEL_NAME });
    const { data: parkingsData } = useListAllParkings({});

    const { data, isLoading, isError } = useListPayStations({
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const columnHelper = createColumnHelper<PayStationDevice>();
    const navigate = useNavigate();

    const columns = [
        columnHelper.accessor('identifier', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(payStationsMessages.identifier),
            },
        }),
        columnHelper.accessor('serial_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(payStationsMessages.serialNumber),
            },
        }),
        columnHelper.accessor('parking.name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            id: 'parking',
            meta: {
                title: formatMessage(payStationsMessages.parking),
                options: parkingsData?.results.map((parking) => ({ value: parking.id, label: parking.name })),
            },
        }),
        columnHelper.accessor('state', {
            header: DataTableColumnHeader,
            cell: (props) => <CellBadge badgeOptions={badgePayStationStateOptions} {...props} />,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(payStationsMessages.state),
                badgeOptions: badgePayStationStateOptions,
            },
        }),
        columnHelper.accessor('app_version', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(payStationsMessages.version),
            },
        }),
        columnHelper.accessor('app_push_update_version', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(payStationsMessages.pushVersion),
            },
        }),
        // columnHelper.accessor('last_heart_beat', {
        //     header: DataTableColumnHeader,
        //     enableSorting: true,
        //     enableHiding: true,
        //     meta: {
        //         title: formatMessage(payStationsMessages.lastHeartBeat),
        //     },
        // }),
    ];

    return (
        <DataTable
            columns={columns}
            data={data}
            pagination={pagination}
            sorting={sorting}
            filters={filters}
            search={search}
            rowSelection={rowSelection}
            semantics={{
                title: formatMessage(payStationsMessages.payStationDevices),
                entryName: formatMessage(payStationsMessages.device),
                entryNames: formatMessage(payStationsMessages.devices),
            }}
            isError={isError}
            isLoading={isLoading}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
            dataTableBodyProps={{
                onRowClickCallback: (row) => {
                    void navigate(`/pay-stations/${row.id}`);
                },
            }}
        />
    );
};

export default AllDevicesTable;
